import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import JsonLd from '@/components/base/JsonLd';
import VideoCard from '@/components/base/VideoCard';
import { updateDocumentMeta } from '@/components/base/SeoHelmet';
import { articles, categories } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [activeSlug, setActiveSlug] = useState(slug || 'movie');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const categoryArticles = useMemo(() => {
    return articles.filter((a) => a.category === activeSlug);
  }, [activeSlug]);

  useEffect(() => {
    updateDocumentMeta(
      `${currentCategory.name} - 红桃视频在线观看`,
      `红桃视频在线观看${currentCategory.name}分类，精选高清视频资源，免费在线观看。每日更新最新${currentCategory.name}内容，即点即播，流畅体验。`,
      `红桃视频在线观看,红桃视频${currentCategory.name},红桃视频免费观看,${currentCategory.name}在线观看,红桃视频高清播放`,
      `https://www.hongtaovideo.com/category/${activeSlug}`
    );
  }, [currentCategory, activeSlug]);

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hongtaovideo.com/' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://www.hongtaovideo.com/category/${activeSlug}` },
    ],
  };

  return (
    <>
      <JsonLd data={breadcrumbJsonLd} />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>

          <section className="pb-14 md:pb-20">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6">
              <div className="mb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {currentCategory.name}
                  <span className="text-base font-normal text-foreground-500 ml-2">分类</span>
                </h1>
                <p className="mt-2 text-sm text-foreground-500">
                  红桃视频在线观看{currentCategory.name}分类 — {currentCategory.description}
                </p>
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-8 p-1 bg-background-100 rounded-full">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {categoryArticles.map((article) => (
                    <VideoCard
                      key={article.id}
                      title={article.title}
                      excerpt={article.excerpt}
                      slug={article.slug}
                      thumbnail={article.thumbnail}
                      category={article.category}
                      categoryName={currentCategory.name}
                      views={article.views}
                      date={article.date}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-20 h-20 mx-auto rounded-full bg-background-200 flex items-center justify-center mb-4">
                    <i className="ri-video-line text-3xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无视频内容，请浏览其他分类</p>
                  <Link
                    to="/category/movie"
                    className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    查看电影分类
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              )}

              {categoryArticles.length > 0 && (
                <div className="mt-12 text-center">
                  <p className="text-sm text-foreground-400">
                    共 {categoryArticles.length} 部{currentCategory.name}视频 —
                    红桃视频在线观看持续更新更多精彩内容
                  </p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}